import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { roleName } from '../config/roleNames';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Multi-role login: choose which role to act as this session. */
export default function RolePicker() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const roles = user?.roles || [];

  const scopeHint = (r) => {
    if (r.league_id) return 'League';
    if (r.org_id) return 'Organization';
    return 'Corporate (HQ)';
  };

  const choose = async (r, i) => {
    setBusy(i);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/active-role`,
        { role_type: r.role_type, org_id: r.org_id || null, league_id: r.league_id || null },
        { withCredentials: true });
      setUser(data);
      const isSemprion = data.roles?.some((x) => x.role_type === 'semprion_admin');
      navigate(isSemprion ? '/admin' : '/dashboard', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not set role');
      setBusy(null);
    }
  };

  if (roles.length <= 1) { navigate('/dashboard', { replace: true }); return null; }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800" data-testid="role-picker">
        <CardHeader>
          <CardTitle className="text-white text-lg">Choose your role</CardTitle>
          <p className="text-sm text-slate-400">This account has multiple roles. Pick one to continue.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {roles.map((r, i) => (
            <button key={`${r.role_type}-${r.org_id}-${r.league_id}-${i}`} type="button"
              disabled={busy !== null} onClick={() => choose(r, i)}
              data-testid={`role-option-${r.role_type}`}
              className="w-full text-left px-4 py-3 rounded-lg border border-slate-700 hover:border-teal-500 hover:bg-teal-600/10 transition-colors flex items-center justify-between disabled:opacity-50">
              <span>
                <span className="text-white font-medium">{roleName(r.role_type)}</span>
                <span className="block text-xs text-slate-400">{scopeHint(r)}</span>
              </span>
              {busy === i && <Loader2 className="h-4 w-4 animate-spin text-teal-400" />}
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
